/**
 * SortableHeader - Clickable column header with sort indicators
 */

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';

interface SortableHeaderProps {
  label: string;
  sortKey: string;
  currentOrdering: string;
  onOrderingChange: (ordering: string) => void;
}

export const SortableHeader = ({
  label,
  sortKey,
  currentOrdering,
  onOrderingChange,
}: SortableHeaderProps) => {
  const isAscending = currentOrdering === sortKey;
  const isDescending = currentOrdering === `-${sortKey}`;
  const isActive = isAscending || isDescending;

  const handleClick = () => {
    if (isAscending) {
      // Switch to descending
      onOrderingChange(`-${sortKey}`);
    } else if (isDescending) {
      // Switch back to default (modified_at desc)
      onOrderingChange('-modified_at');
    } else {
      // Start with ascending
      onOrderingChange(sortKey);
    }
  };

  const renderSortIcon = () => {
    if (!isActive) {
      return (
        <div className="h-4 w-4 opacity-0 group-hover:opacity-50 transition-opacity">
          <ChevronUpIcon className="h-4 w-4 text-gray-400" />
        </div>
      );
    }

    if (isAscending) {
      return <ChevronUpIcon className="h-4 w-4 text-gray-700" />;
    }

    return <ChevronDownIcon className="h-4 w-4 text-gray-700" />;
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group flex items-center gap-1 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700 transition-colors"
    >
      <span>{label}</span>
      {renderSortIcon()}
    </button>
  );
};
